import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import getOptions from '../utilities/getOptions';
import getDisplayValue from '../utilities/getDisplayValue';
import getValue from '../utilities/getValue';
import useHighlight from './useHighlight';
import transformOptions from '../utilities/transformOptions';
import isValueChange from '../utilities/isValueChange';

/**
 * A hook that manages all the stateful logic needed to make the dropdown functional and accessible.
 * It returns common value,children props
 * These props are called getter props and their return values are destructured as a set of attributes and event listeners.
 *
 * @param {Object} obj - An object.
 * @param {string} obj.value
 * @param {string} obj.facetId
 * @param {[]} obj.options
 * @param {function} obj.onChange - Trigger on select option
 * @param {function} obj.onFocus
 * @param {function} obj.onBlur
 * @param {function} obj.onOpened
 * @returns {*[]} - Array like [snapshot, valueProps, optionProps]
 */
export default function useSelect({
	value: selectedValue = null,
	facetId = null,
	options: defaultOptions = [],
	closeOnSelect = true,
	onChange = () => {},
	onFocus = () => {},
	onBlur = () => {},
	onOpened = () => {}
}) {
	const ref = useRef(null);
	const [focus, setFocus] = useState(false);
	const options = useMemo(
		() => transformOptions(defaultOptions),
		[defaultOptions]
	);

	const [selectedOption, setSelectedOption] = useState(() =>
		getOptions(selectedValue, null, options)
	);
	const onSelect = useCallback(
		(newValue) => {
			const newOption = getOptions(newValue, selectedOption, options);
			if (isValueChange(newOption, selectedOption)) {
				setSelectedOption(newOption);
				const facet = {
					newValue: getValue(newOption),
					newOption,
					facetId
				};
				onChange(facet);
			}

			if (closeOnSelect) {
				ref.current.blur();
			}
		},
		[closeOnSelect, onChange, selectedOption, options, facetId]
	);

	const [highlighted, keyboardEvents, resetHighlighted] = useHighlight(
		-1,
		options,
		onSelect,
		ref,
		focus
	);
	const snapshot = useMemo(
		() => ({
			options,
			option: selectedOption,
			displayValue: getDisplayValue(selectedOption),
			value: getValue(selectedOption),
			focus,
			highlighted
		}),
		[focus, highlighted, selectedOption, options]
	);

	const onMouseDown = useCallback(
		(e) => {
			e.preventDefault();
			onSelect(e.currentTarget.value);
		},
		[onSelect]
	);

	const onClickCB = useCallback((e) => {
		e.preventDefault();
		setFocus((prev) => !prev);
	}, []);

	const onBlurCb = useCallback(
		(e) => {
			setFocus(false);
			onBlur(e);
		},
		[onBlur]
	);

	const valueProps = useMemo(
		() => ({
			tabIndex: '0',
			readOnly: true,
			...keyboardEvents,
			onBlur: onBlurCb,
			onClick: onClickCB,
			ref
		}),
		[keyboardEvents, onBlurCb, onClickCB]
	);

	const optionProps = useMemo(
		() => ({
			tabIndex: '-1',
			onMouseDown
		}),
		[onMouseDown]
	);

	useEffect(() => {
		if (focus) {
			resetHighlighted();
			onOpened();
		} else {
			ref.current.blur();
		}
	}, [focus, onFocus, onOpened, resetHighlighted]);

	useEffect(() => {
		setSelectedOption(getOptions(selectedValue, null, options));
	}, [selectedValue, options]);

	return [snapshot, valueProps, optionProps];
}
